
import React, { useState, useRef, useEffect } from "react"
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../ui/tooltip"
import {
  Menu, X, ChevronDown, LogIn, UserPlus,
  Search, ChevronUp, ChevronDown as ChevronDownIcon,
  BookOpen, GraduationCap, Briefcase,
} from "lucide-react"
import Logo from "../../assets/fillopsLogo.png"
import { Link } from "react-router-dom"
import { useSearch } from "./SearchContext"
import CartIcon from "../CartIcon"

// ── Start Learning Dropdown ───────────────────────────────────────────────────

function StartLearningDropdown() {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center bg-sidebar rounded outline-none  px-3 py-2 gap-1 text-white  text-sm font-medium cursor-pointer transition-colors"
      >
        Start Learning
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-100 rounded shadow-lg py-1.5 z-50 overflow-hidden animate-[fadeDown_0.18s_ease_both]">

          <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">Courses</p>

          <a
            href="https://study.filloptech.com"
             target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-800 no-underline transition-colors"
          >
            <span className="w-6 h-6 rounded-full bg-yellow-50 flex items-center justify-center flex-shrink-0">
              <BookOpen size={13} className="text-yellow-600" />
            </span>
            WAEC / NECO
          </a>

          <a
            href="https://study.filloptech.com"
             target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-800 no-underline transition-colors"
          >
            <span className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
              <GraduationCap size={13} className="text-blue-600" />
            </span>
            JAMB
          </a>

          <Link
                      to="/login"
            onClick={() => setOpen(false)}
            className="flex items-center cursor-pointer gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-800 no-underline transition-colors"
          >
            <span className="w-6 h-6  rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
              <Briefcase size={13} className="text-purple-600" />
            </span>
            Professional Skills
          </Link>

          <div className="h-px bg-gray-100 mx-3 my-1" />


        </div>
      )}
    </div>
  )
}

// ── Resource Person Dropdown ──────────────────────────────────────────────────
// Contains: Become a Resource Person, Resource Person Login
function ResourcePersonDropdown() {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-gray-600 hover:text-sidebar text-sm font-medium cursor-pointer transition-colors"
      >
        Become a Resource Person
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-100 rounded shadow-lg py-1.5 z-50 overflow-hidden animate-[fadeDown_0.18s_ease_both]">

          <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">Resource Person</p>

          <Link
            to="/resource-person"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-800 no-underline transition-colors"
          >
            <span className="w-6 h-6 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
              <UserPlus size={13} className="text-green-600" />
            </span>
            Become a Resource Person
          </Link>

          <Link
            to="/resource-personlogin"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-800 no-underline transition-colors"
          >
            <span className="w-6 h-6 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <LogIn size={13} className="text-indigo-600" />
            </span>
            Resource Person Login
          </Link>
        </div>
      )}
    </div>
  )
}

// ── In-page Search Bar ────────────────────────────────────────────────────────
function InPageSearch({ onClose, autoFocus = false }) {
  const { query, updateQuery, matchCount, currentMatch, goToNext, goToPrev, clearSearch, registerMatches } = useSearch()
  const inputRef = useRef(null)

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      registerMatches([])
      return
    }
    const timer = setTimeout(() => {
      const marks = Array.from(document.querySelectorAll("mark.search-hit"))
      registerMatches(marks)
    }, 60)
    return () => clearTimeout(timer)
  }, [query])

  const handleChange = (e) => updateQuery(e.target.value)

  const handleKeyDown = (e) => {
    if (e.key === "Enter") { e.shiftKey ? goToPrev() : goToNext() }
    if (e.key === "Escape") { clearSearch(); onClose?.() }
  }

  const handleClear = () => { clearSearch(); onClose?.() }

  const hasQuery  = query.length >= 2
  const hasResult = matchCount > 0
  const noResult  = hasQuery && matchCount === 0

  return (
    <div className="flex items-center gap-2">
      <div className={`flex items-center gap-2 rounded-full px-3.5 py-1.5 border transition-all duration-200 bg-white
        ${noResult
          ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]"
          : hasResult
            ? "border-blue-400 shadow-[0_0_0_3px_rgba(59,130,246,0.12)]"
            : "border-gray-200 hover:border-gray-300"
        }`}
      >
        <Search size={14} className={`shrink-0 ${noResult ? "text-red-400" : "text-gray-400"}`} />
        <input
          ref={inputRef}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Search on this page…"
          className="text-sm text-gray-800 placeholder:text-gray-400 outline-none bg-transparent w-44 sm:w-56"
        />
        {hasQuery && (
          <span className={`text-[11px] font-semibold shrink-0 ${noResult ? "text-red-400" : "text-blue-600"}`}>
            {noResult ? "No results" : `${currentMatch}/${matchCount}`}
          </span>
        )}
        {query && (
          <button onClick={handleClear} className="text-gray-300 hover:text-gray-500 transition-colors shrink-0">
            <X size={13} />
          </button>
        )}
      </div>

      {hasResult && (
        <div className="flex items-center gap-0.5">
          <button onClick={goToPrev} title="Previous (Shift+Enter)"
            className="p-1.5 rounded-lg text-gray-500 hover:text-blue-700 hover:bg-blue-50 transition-colors">
            <ChevronUp size={15} />
          </button>
          <button onClick={goToNext} title="Next (Enter)"
            className="p-1.5 rounded-lg text-gray-500 hover:text-blue-700 hover:bg-blue-50 transition-colors">
            <ChevronDownIcon size={15} />
          </button>
        </div>
      )}

      {onClose && (
        <button onClick={handleClear}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
          <X size={15} />
        </button>
      )}
    </div>
  )
}

// ── Main Navbar ────────────────────────────────────────────────────────────────
export default function Navbar() {
  const [menuOpen,           setMenuOpen]           = useState(false)
  const [mobileStartOpen,    setMobileStartOpen]    = useState(false)
  const [mobileRPOpen,       setMobileRPOpen]       = useState(false)
  const [searchOpen,         setSearchOpen]         = useState(false)
  const { clearSearch } = useSearch()

  const closeSearch = () => { setSearchOpen(false); clearSearch() }

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault(); setSearchOpen(true)
      }
      if (e.key === "/" && e.target.tagName !== "INPUT" && e.target.tagName !== "TEXTAREA") {
        e.preventDefault(); setSearchOpen(true)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  return (
    <>
      <TooltipProvider>
        <nav className="sticky w-full top-0 bg-white/90 backdrop-blur border-b border-gray-200 z-[99999999]">

          <div className="flex items-center py-3 px-4 gap-4 z-[99999999] ">

            {/* Logo */}
            <div className="flex items-center gap-2 text-2xl text-sidebar font-bold shrink-0">
              <img src={Logo} alt="Fillops" className="w-8 sm:w-10" />
              <span className="text-[#0e3b9a]">Fillop</span>
            </div>

            {/* ── Desktop ── */}
            <div className="hidden md:flex items-center gap-4 flex-1">

              <div className="flex-1" />

              {/* Search */}
              <div className="flex items-center pr-10">
                {searchOpen ? (
                  <InPageSearch onClose={closeSearch} autoFocus />
                ) : (
                  <button
                    onClick={() => setSearchOpen(true)}
                    title="Search on page (/ or Ctrl+F)"
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 hover:border-gray-300 bg-gray-50 hover:bg-white rounded-full pl-3 pr-4 py-1.5 transition-all"
                  >
                    <Search size={14} />
                    <span className="text-gray-400 text-xs">Search…</span>
                    <kbd className="hidden lg:inline-flex items-center text-[10px] text-gray-300 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 font-mono ml-1">/</kbd>
                  </button>
                )}
              </div>

              {/* Nav links */}
              <div className="flex items-center gap-6 lg:gap-8">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href="https://business.filloptech.com"
                      onClick={() => { setMenuOpen(false); setMobileStartOpen(false) }}
                      className="text-gray-600 no-underline hover:text-sidebar text-sm font-medium cursor-pointer transition-colors"
                    >
                      Our Business
                    </a>
                  </TooltipTrigger>
                  <TooltipContent><p>Our Business</p></TooltipContent>
                </Tooltip>

                {/* Become a Resource Person — dropdown */}
                <ResourcePersonDropdown />

                <CartIcon />

                <StartLearningDropdown />

                <Link
                  to="/login"
                  onClick={() => { setMenuOpen(false); setMobileStartOpen(false) }}
                  className="text-gray-600 no-underline hover:text-sidebar text-sm font-medium cursor-pointer transition-colors"
                >
                  Login
                </Link>
              </div>
            </div>

            {/* Mobile icons */}
            <div className="flex items-center gap-1 md:hidden ml-auto">
              <CartIcon />
              <button
                onClick={() => setSearchOpen(o => !o)}
                className={`p-2 rounded-xl transition-colors ${searchOpen ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-100"}`}
                aria-label="Search"
              >
                <Search size={20} />
              </button>
              <button
                className="p-2 rounded-lg hover:bg-gray-100 transition"
                onClick={() => setMenuOpen(o => !o)}
                aria-label="Menu"
              >
                {menuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>

          {/* Mobile search bar */}
          {searchOpen && (
            <div className="md:hidden border-t border-gray-100 bg-white px-4 py-3 flex items-center gap-2 animate-[fadeDown_0.18s_ease_both]">
              <InPageSearch onClose={closeSearch} autoFocus />
            </div>
          )}

          {/* Mobile Menu */}
          {menuOpen && (
            <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 flex flex-col gap-2 animate-[fadeDown_0.18s_ease_both]">
                <Link
                  to="/login"
                  onClick={() => { setMenuOpen(false); setMobileStartOpen(false) }}
                  className="text-gray-600 no-underline hover:text-sidebar text-sm font-medium transition-colors"
                >
                  Login
                </Link>

                <a
                  href="https://business.filloptech.com"
                  onClick={() => { setMenuOpen(false); setMobileStartOpen(false) }}
                  className="text-gray-600 no-underline hover:text-sidebar text-sm font-medium cursor-pointer transition-colors"
                >
                  Our Business
                </a>

              {/* Become a Resource Person — accordion in mobile menu */}
              <div className="border-t border-gray-100 pt-3">
                <button
                  onClick={() => setMobileRPOpen(o => !o)}
                  className="w-full flex items-center justify-between text-gray-700 font-medium py-1 hover:text-blue-800 transition-colors"
                >
                  <span>Become a Resource Person</span>
                  <ChevronDown size={15} className={`transition-transform duration-200 ${mobileRPOpen ? "rotate-180" : ""}`} />
                </button>

                {mobileRPOpen && (
                  <div className="mt-2 ml-3 flex flex-col gap-1 border-l-2 border-green-100 pl-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 pt-1 pb-0.5">Resource Person</p>
                    <Link to="/resource-person"
                      onClick={() => { setMenuOpen(false); setMobileRPOpen(false) }}
                      className="flex items-center gap-2 text-sm text-gray-600 py-1.5 no-underline hover:text-blue-800 transition-colors">
                      <span className="w-5 h-5 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                        <UserPlus size={11} className="text-green-600" />
                      </span>
                      Become a Resource Person
                    </Link>
                    <Link to="/tutor-login"
                      onClick={() => { setMenuOpen(false); setMobileRPOpen(false) }}
                      className="flex items-center gap-2 text-sm text-gray-600 py-1.5 no-underline hover:text-blue-800 transition-colors">
                      <span className="w-5 h-5 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                        <LogIn size={11} className="text-indigo-600" />
                      </span>
                      Resource Person Login
                    </Link>
                  </div>
                )}
              </div>

              {/* Start Learning accordion */}
              <div>
                <button
                  onClick={() => setMobileStartOpen(o => !o)}
                  className="w-full flex items-center justify-between text-gray-700 font-medium py-1 hover:text-blue-800 transition-colors"
                >
                  <span>Start Learning</span>
                  <ChevronDown size={15} className={`transition-transform duration-200 ${mobileStartOpen ? "rotate-180" : ""}`} />
                </button>

                {mobileStartOpen && (
                  <div className="mt-2 ml-3 flex flex-col gap-1 border-l-2 border-blue-100 pl-3">

                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 pt-1 pb-0.5">Courses</p>

                    <a href="https://study.filloptech.com"
                      target="_parent"
                      rel="noopener noreferrer"
                      onClick={() => { setMenuOpen(false); setMobileStartOpen(false) }}
                      className="flex items-center gap-2 text-sm text-gray-600 py-1.5 no-underline hover:text-blue-800 transition-colors">
                      <span className="w-5 h-5 rounded-full bg-yellow-50 flex items-center justify-center flex-shrink-0">
                        <BookOpen size={11} className="text-yellow-600" />
                      </span>
                      WAEC / NECO
                    </a>

                    <a href="https://study.filloptech.com"
                      target="_parent"
                      rel="noopener noreferrer"
                      onClick={() => { setMenuOpen(false); setMobileStartOpen(false) }}
                      className="flex items-center gap-2 text-sm text-gray-600 py-1.5 no-underline hover:text-blue-800 transition-colors">
                      <span className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <GraduationCap size={11} className="text-blue-600" />
                      </span>
                      JAMB
                    </a>

                    <Link to="/login"
                      target="_parent"
                      rel="noopener noreferrer"
                      onClick={() => { setMenuOpen(false); setMobileStartOpen(false) }}
                      className="flex items-center cursor-pointer gap-2 text-sm text-gray-600 py-1.5 no-underline hover:text-blue-800 transition-colors">
                      <span className="w-5 h-5 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                        <Briefcase size={11} className="text-purple-600" />
                      </span>
                      Professional Skills
                    </Link>

                    <div className="h-px bg-gray-100 my-1" />
                  </div>
                )}
              </div>

            </div>
          )}
        </nav>
      </TooltipProvider>
    </>
  )
}
